import { useEffect, useMemo, useState } from 'react'
import * as S from './TransferTicketModal.styles'
import { Button } from 'applaus-ui-kit'
import { useFormik } from 'formik'
import { passwordSchema, recipientSchema } from './validationSchema'
import { CustomerTickets, PurchaseOrder } from 'redux/Purchases/types'
import moment from 'moment'
import {
  createTicketTransferService,
  getTransferPasswordErrorMessage,
  validateTransferRecipientService
} from 'api/services/ticketTransfer/ticketTransferService'

type Props = {
  isOpen: boolean
  handleClose: () => void
  purchaseOrder: PurchaseOrder
  onSuccess?: () => void
}

const StepMap = {
  1: 'Informações do ingresso sendo transferido',
  2: 'Informar para quem será transferido',
  3: 'Autenticação do usuário',
  4: 'Transferência realizada com sucesso!'
}

const isTicketTransferable = (ticket: CustomerTickets) => {
  if (ticket.detachedAt) return false
  if (ticket.isValid === false) return false
  if (ticket.status && ticket.status !== 'ACTIVE') return false
  const dates = ticket.eventItem?.dates
  if (dates?.length) {
    const earliest = moment.min(dates.map((d) => moment(d)))
    if (!earliest.isAfter(moment())) return false
  }
  return true
}

export const TransferTicketModal = ({
  isOpen,
  handleClose,
  purchaseOrder,
  onSuccess
}: Props) => {
  const [currStep, setCurrStep] = useState<1 | 2 | 3 | 4>(1)
  const [selectedTicketId, setSelectedTicketId] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const transferableTickets = useMemo(
    () => (purchaseOrder?.customerTickets ?? []).filter(isTicketTransferable),
    [purchaseOrder]
  )

  const selectedTicket = transferableTickets.find(
    (ticket) => ticket.id === selectedTicketId
  )

  const recipientForm = useFormik({
    initialValues: {
      email: '',
      fullName: '',
      documentNumber: ''
    },
    validationSchema: recipientSchema,
    onSubmit: async (values) => {
      setErrorMessage('')
      setIsSubmitting(true)
      try {
        await validateTransferRecipientService({
          email: values.email,
          fullName: values.fullName,
          document: values.documentNumber
        })
        setCurrStep(3)
      } catch (error: any) {
        setErrorMessage(
          error?.response?.data?.message?.message ||
            error?.response?.data?.message ||
            'Destinatário não encontrado com este nome, e-mail e CPF.'
        )
      } finally {
        setIsSubmitting(false)
      }
    }
  })

  const passwordForm = useFormik({
    initialValues: {
      password: '',
      confirmPassword: ''
    },
    validationSchema: passwordSchema,
    onSubmit: async (values) => {
      if (!selectedTicket) return
      setErrorMessage('')
      setIsSubmitting(true)
      try {
        await createTicketTransferService({
          customerTicketId: selectedTicket.id,
          email: recipientForm.values.email,
          fullName: recipientForm.values.fullName,
          document: recipientForm.values.documentNumber,
          password: values.password
        })
        setCurrStep(4)
        onSuccess?.()
      } catch (error: any) {
        setErrorMessage(getTransferPasswordErrorMessage(error))
        passwordForm.setFieldValue('password', '')
        passwordForm.setFieldValue('confirmPassword', '')
      } finally {
        setIsSubmitting(false)
      }
    }
  })

  useEffect(() => {
    if (!isOpen) return
    if (transferableTickets.length === 1) {
      setSelectedTicketId(transferableTickets[0].id)
    }
  }, [isOpen, transferableTickets])

  const clearOnClose = () => {
    setCurrStep(1)
    setSelectedTicketId(
      transferableTickets.length === 1 ? transferableTickets[0].id : ''
    )
    setErrorMessage('')
    recipientForm.resetForm()
    passwordForm.resetForm()
    handleClose()
  }

  const goToStep2 = () => {
    setErrorMessage('')
    if (!selectedTicketId) {
      setErrorMessage('Selecione o ingresso que deseja transferir.')
      return
    }
    setCurrStep(2)
  }

  return (
    <S.Container isOpen={isOpen}>
      <S.Content>
        <p>{StepMap[currStep]}</p>

        {errorMessage ? <S.ErrorText>{errorMessage}</S.ErrorText> : null}

        {currStep === 1 && (
          <S.StepContainer>
            <p>{purchaseOrder.eventItem.event.name}</p>
            <p>
              {moment(purchaseOrder.eventItem.dates[0]).format('DD/MM/YYYY')}
            </p>
            <p>
              {purchaseOrder.eventItem.event.address.street} -{' '}
              {purchaseOrder.eventItem.event.address.city}
            </p>

            {transferableTickets.length === 0 ? (
              <p>Nenhum ingresso disponível para transferência nesta compra.</p>
            ) : (
              <S.TicketList>
                {transferableTickets.map((ticket) => (
                  <S.TicketOption
                    key={ticket.id}
                    type="button"
                    selected={selectedTicketId === ticket.id}
                    onClick={() => setSelectedTicketId(ticket.id)}>
                    <strong>{ticket.ticket.name}</strong>
                    <span>{ticket.customerTicketId}</span>
                  </S.TicketOption>
                ))}
              </S.TicketList>
            )}

            <S.ButtonsContainer>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                color="primary"
                onClick={clearOnClose}>
                Cancelar
              </Button>
              <Button
                fullWidth
                variant="contained"
                size="small"
                color="primary"
                disabled={transferableTickets.length === 0}
                onClick={goToStep2}>
                Transferir ingresso
              </Button>
            </S.ButtonsContainer>
          </S.StepContainer>
        )}

        {currStep === 2 && (
          <S.StepContainer>
            <S.InputWrapper>
              <S.StyledInput
                name="fullName"
                placeholder="Nome completo do destinatário"
                value={recipientForm.values.fullName}
                onChange={recipientForm.handleChange}
                onBlur={recipientForm.handleBlur}
              />
            </S.InputWrapper>
            <S.InputWrapper>
              <S.StyledInput
                name="email"
                placeholder="Email do destinatário"
                value={recipientForm.values.email}
                onChange={recipientForm.handleChange}
                onBlur={recipientForm.handleBlur}
              />
            </S.InputWrapper>
            <S.InputWrapper>
              <S.StyledInput
                name="documentNumber"
                placeholder="CPF do destinatário"
                value={recipientForm.values.documentNumber}
                onChange={recipientForm.handleChange}
                onBlur={recipientForm.handleBlur}
              />
            </S.InputWrapper>

            <S.ButtonsContainer>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                color="primary"
                onClick={clearOnClose}>
                Cancelar
              </Button>
              <Button
                fullWidth
                variant="contained"
                size="small"
                color="primary"
                disabled={isSubmitting}
                onClick={() => recipientForm.handleSubmit()}>
                Continuar
              </Button>
            </S.ButtonsContainer>
          </S.StepContainer>
        )}

        {currStep === 3 && (
          <S.StepContainer>
            <p>Confirme sua identidade</p>
            <S.InputWrapper>
              <S.StyledInput
                name="password"
                type="password"
                placeholder="Digite sua senha"
                value={passwordForm.values.password}
                onChange={passwordForm.handleChange}
                onBlur={passwordForm.handleBlur}
              />
            </S.InputWrapper>
            <S.InputWrapper>
              <S.StyledInput
                name="confirmPassword"
                type="password"
                placeholder="Confirme sua senha"
                value={passwordForm.values.confirmPassword}
                onChange={passwordForm.handleChange}
                onBlur={passwordForm.handleBlur}
              />
            </S.InputWrapper>

            <S.ButtonsContainer>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                color="primary"
                onClick={clearOnClose}>
                Cancelar
              </Button>
              <Button
                fullWidth
                variant="contained"
                size="small"
                color="primary"
                disabled={isSubmitting}
                onClick={() => passwordForm.handleSubmit()}>
                Confirmar transferência
              </Button>
            </S.ButtonsContainer>
          </S.StepContainer>
        )}

        {currStep === 4 && (
          <S.StepContainer>
            <p>
              O destinatário receberá um email com as informações do ingresso.
            </p>

            <S.ButtonsContainer>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                color="primary"
                onClick={clearOnClose}>
                Fechar
              </Button>
            </S.ButtonsContainer>
          </S.StepContainer>
        )}
      </S.Content>
    </S.Container>
  )
}
